import { debounce } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useLoggedInUserRoles } from '~/components/Auth';
import { Employees } from '~/data/employees/types';
import {
  isRoleDepManager,
  isRoleHR,
  isRoleHRAdmin,
  isRoleITAdmin,
  isRoleManager,
  isRoleRecruiter,
} from '~/services/Permissions';

import { GroupedOrgUnitEmps, useSearchData } from './useSearchData';
import {
  ALL_EMPS_SEARCH_TAB_INDEX,
  ENDED_EMPS_SEARCH_TAB_INDEX,
  HR_ADMIN_OVERVIEW_SEARCH_TAB_INDEX,
  MATRIX_TEAM_SEARCH_TAB_INDEX,
  NEW_EMPS_SEARCH_TAB_INDEX,
  ORG_UNITS_SEARCH_TAB_INDEX,
  RESULTS_SEARCH_TAB_INDEX,
  SEND_EMAIL_SEARCH_TAB_INDEX,
  TEAM_SEARCH_TAB_INDEX,
  useSearchUI,
} from './useSearchUI';

export type UseEmployeesSearchOutput = {
  currentTab: number;
  listItems: Employees[] | GroupedOrgUnitEmps[];
  isBoxOpen: boolean;
  isPinned: boolean;
  isHRAdmin: boolean;
  showEditableEndedEmployeeTab: boolean;
  showMatrixTeamTab: boolean;
  showMyTeamTab: boolean;
  showNewEmployeeTab: boolean;
  onClose: (force?: boolean) => void;
  onListToggle: () => void;
  onPin: () => void;
  onTabChange: (tabIndex: number) => void;
  onSearch: (e: ChangeEvent<HTMLInputElement>) => void;
};

const DEBOUNCE_TIMER_MS = 400;
export const useEmployeesSearch = (
  onEmailClick: () => void,
): UseEmployeesSearchOutput => {
  const myRoles = useLoggedInUserRoles();
  const {
    currentTab,
    isBoxOpen,
    isPinned,
    openBox,
    onClose,
    toggleBox,
    onCurrentTabChange,
    onPin,
  } = useSearchUI(onEmailClick);
  const {
    allEmployees,
    hasMatrixTeam,
    showEditableEndedEmployeeTab,
    getSearchResults,
    getTeamMemberList,
    getMatrixTeamMemberList,
    getNewEmployeeList,
    getEditableEndedEmpList,
    getGroupedOrgUnitEmployees,
  } = useSearchData();
  const isHRAdmin: boolean = useMemo(() => isRoleHRAdmin(myRoles), [myRoles]);
  const [listItems, setListItems] = useState<
    Employees[] | GroupedOrgUnitEmps[]
  >([]);
  const [searchQuery, setSearchQuery] = useState('');
  const showMyTeamTab = useMemo(
    () => isRoleDepManager(myRoles) || isRoleManager(myRoles),
    [myRoles],
  );

  const showNewEmployeeTab = useMemo(
    () =>
      isRoleHR(myRoles) ||
      isRoleITAdmin(myRoles) ||
      isRoleManager(myRoles) ||
      isRoleRecruiter(myRoles),
    [myRoles],
  );

  const showDefaultTabContent = useCallback(() => {
    onCurrentTabChange(ALL_EMPS_SEARCH_TAB_INDEX);
    setListItems(allEmployees);
  }, [onCurrentTabChange, setListItems, allEmployees]);

  useEffect(() => {
    if (!isBoxOpen && isPinned) {
      showDefaultTabContent();
    }
  }, [isBoxOpen, isPinned, showDefaultTabContent]);

  const onListToggle = () => {
    if (!isBoxOpen) {
      showDefaultTabContent();
    }

    toggleBox();
  };

  const handleSearch = useCallback(
    (searchQuery: string) => {
      if (!searchQuery) {
        setListItems([]);
        return;
      }

      setListItems(getSearchResults(searchQuery));
      openBox();
    },
    [getSearchResults, setListItems, openBox],
  );

  // https://github.com/facebook/react/issues/19240#issuecomment-652945246
  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        setSearchQuery(query);
        onCurrentTabChange(RESULTS_SEARCH_TAB_INDEX);
        handleSearch(query);
      }, DEBOUNCE_TIMER_MS),
    [setSearchQuery, onCurrentTabChange, handleSearch],
  );

  const updateListItems = useCallback(
    (tabIndex: number) => {
      switch (tabIndex) {
        case ALL_EMPS_SEARCH_TAB_INDEX:
          return setListItems(allEmployees);
        case TEAM_SEARCH_TAB_INDEX:
          return setListItems(getTeamMemberList());
        case MATRIX_TEAM_SEARCH_TAB_INDEX:
          return setListItems(getMatrixTeamMemberList());
        case ORG_UNITS_SEARCH_TAB_INDEX:
          return setListItems(getGroupedOrgUnitEmployees());
        case NEW_EMPS_SEARCH_TAB_INDEX:
          return setListItems(getNewEmployeeList());
        case ENDED_EMPS_SEARCH_TAB_INDEX:
          return setListItems(getEditableEndedEmpList());
        case RESULTS_SEARCH_TAB_INDEX:
          return handleSearch(searchQuery);
        case SEND_EMAIL_SEARCH_TAB_INDEX:
          return;
        case HR_ADMIN_OVERVIEW_SEARCH_TAB_INDEX:
          return;
        default:
          return;
      }
    },
    [
      allEmployees,
      searchQuery,
      getGroupedOrgUnitEmployees,
      getTeamMemberList,
      getMatrixTeamMemberList,
      getNewEmployeeList,
      getEditableEndedEmpList,
      handleSearch,
      setListItems,
    ],
  );

  const onTabChange = (tabIndex: number) => {
    onCurrentTabChange(tabIndex);
    updateListItems(tabIndex);
  };

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  return {
    currentTab,
    listItems,
    isBoxOpen,
    isPinned,
    isHRAdmin,
    showEditableEndedEmployeeTab,
    showMatrixTeamTab: hasMatrixTeam,
    showMyTeamTab,
    showNewEmployeeTab,
    onClose,
    onListToggle,
    onPin,
    onTabChange,
    onSearch,
  };
};
